import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  coursesApi,
  groupRoundsApi,
  guestPlayersApi,
  playersApi,
  roundsApi,
  socialApi,
  usersApi,
} from './endpoints';
import type { CourseSummary, Paginated, Round, RoundStatus, RoundSummary } from './types';

/**
 * Query keys. Grouped so a mutation can invalidate a whole branch.
 */
export const qk = {
  me: ['me'] as const,
  handicap: ['handicap'] as const,
  rounds: (params?: object) => ['rounds', params ?? {}] as const,
  allRounds: ['rounds', 'all'] as const,
  round: (id: string) => ['round', id] as const,
  courseIndex: ['courses', 'index'] as const,
  course: (id: string) => ['course', id] as const,
  nearby: (lng: number, lat: number) => ['courses', 'nearby', lng, lat] as const,
  feed: (limit: number) => ['feed', limit] as const,
  friends: ['friends'] as const,
  friendRequests: ['friends', 'requests'] as const,
  userSearch: (q: string) => ['users', 'search', q] as const,
  comments: (roundId: string) => ['round', roundId, 'comments'] as const,
  groupRound: (id: string) => ['group-round', id] as const,
  groupRoundByRound: (roundId: string) => ['group-round', 'by-round', roundId] as const,
  guestPlayers: ['guest-players'] as const,
  playingPartners: ['group-rounds', 'partners'] as const,
  playerProfile: (userId: string) => ['player', userId] as const,
  playerRounds: (userId: string) => ['player', userId, 'rounds'] as const,
  roundsTogether: (userId: string) => ['player', userId, 'rounds-together'] as const,
};

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: usersApi.me });
}

export function useHandicap() {
  return useQuery({ queryKey: qk.handicap, queryFn: usersApi.handicap });
}

export function useRound(id: string | undefined) {
  return useQuery({
    queryKey: qk.round(id ?? ''),
    queryFn: () => roundsApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCourse(id: string | undefined) {
  return useQuery({
    queryKey: qk.course(id ?? ''),
    queryFn: () => coursesApi.get(id as string),
    enabled: Boolean(id),
    // A course's holes and GeoJSON are a large payload and change almost never.
    staleTime: 30 * 60_000,
  });
}

const PAGE_LIMIT = 100;
/** Hard stop so a runaway `total` cannot turn a page load into hundreds of requests. */
const MAX_PAGES = 20;

/** Walks a paginated endpoint to completion, bounded. */
async function fetchAllPages<T>(
  fetchPage: (page: number, limit: number) => Promise<Paginated<T>>,
): Promise<{ items: T[]; truncated: boolean }> {
  const first = await fetchPage(1, PAGE_LIMIT);
  const items = [...first.items];
  const totalPages = Math.ceil(first.total / PAGE_LIMIT);
  const pagesToFetch = Math.min(totalPages, MAX_PAGES);

  for (let page = 2; page <= pagesToFetch; page++) {
    const next = await fetchPage(page, PAGE_LIMIT);
    items.push(...next.items);
  }

  return { items, truncated: totalPages > MAX_PAGES };
}

/**
 * Every id → course lookup the app needs.
 *
 * `RoundSummaryDto` carries `courseId` but **not the course name**, so the rounds list and
 * the home screen have nothing to display without this. Fetching the course list once and
 * resolving locally beats an N+1 of `GET /courses/:id` — each of those returns the full
 * GeoJSON for every hole, which is megabytes for a name.
 */
export function useCourseIndex(): UseQueryResult<Map<string, CourseSummary>> {
  return useQuery({
    queryKey: qk.courseIndex,
    queryFn: async () => {
      const { items } = await fetchAllPages((page, limit) => coursesApi.list({ page, limit }));
      return new Map(items.map((c) => [c.id, c]));
    },
    staleTime: 30 * 60_000,
  });
}

/** Resolves a course name, with a graceful stand-in while the index loads. */
export function courseNameFrom(
  index: Map<string, CourseSummary> | undefined,
  courseId: string,
): string {
  return index?.get(courseId)?.name ?? 'Campo';
}

export function useRoundsPage(params: { status?: RoundStatus; page?: number; limit?: number }) {
  return useQuery({
    queryKey: qk.rounds(params),
    queryFn: () => roundsApi.list(params),
    refetchInterval: 20_000,
  });
}

/**
 * Every round the user has.
 *
 * `GET /rounds` only filters by status — period, course and hole-count filters (canvas 1n)
 * and the whole statistics screen have to work over the full set client-side. Since the
 * stats screen needs this anyway, one cached copy serves both.
 */
export function useAllRounds() {
  return useQuery({
    queryKey: qk.allRounds,
    refetchInterval: 20_000,
    queryFn: async () => {
      const { items, truncated } = await fetchAllPages((page, limit) =>
        roundsApi.list({ page, limit }),
      );
      return { rounds: items as RoundSummary[], truncated };
    },
  });
}

/**
 * Full detail for a set of rounds.
 *
 * Unavoidable N+1, and the reason the rate limit had to be fixed first: `RoundSummaryDto`
 * carries no `stats` and no `playerHandicapAtStart`, so anything about putts, GIR, fairways
 * or handicap movement can only come from `GET /rounds/:id`, one call per round.
 *
 * Each result is cached under the same key as `useRound`, so Início, Estatísticas and a
 * round's own detail page all share one copy and warm each other.
 */
export function useRoundDetails(ids: string[]) {
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: qk.round(id),
      queryFn: () => roundsApi.get(id),
      staleTime: 5 * 60_000,
    })),
  });

  const rounds = results.map((r) => r.data).filter((r): r is Round => r !== undefined);

  return {
    rounds,
    isLoading: results.some((r) => r.isLoading),
    // Partial data is still useful — a chart can draw what arrived and say so.
    loadedCount: rounds.length,
    total: ids.length,
    error: results.find((r) => r.error)?.error ?? null,
  };
}

// ---------------------------------------------------------------- social

/**
 * A timeline paginada em uma página crescente, como a lista de voltas: `limit` sobe quando o
 * usuário pede mais. Uma única chave de cache, então reagir ou comentar invalida o feed inteiro
 * numa linha — e não há scroll infinito em lugar nenhum do app.
 *
 * Consulta também quando não há volta ao vivo: um amigo pode começar um flight enquanto
 * esta tela está aberta. O polling pausa automaticamente com a aba em segundo plano.
 */
export function useFeed(limit: number) {
  return useQuery({
    queryKey: qk.feed(limit),
    queryFn: () => socialApi.feed({ page: 1, limit }),
    staleTime: 30_000,
    refetchInterval: 20_000,
  });
}

export function useFriends() {
  return useQuery({ queryKey: qk.friends, queryFn: socialApi.friends });
}

export function useFriendRequests() {
  return useQuery({ queryKey: qk.friendRequests, queryFn: socialApi.requests });
}

/**
 * "Voltas juntos", indexado por pessoa. Um `Map` porque quem consome pergunta por id, e a
 * ausência é resposta legítima: zero voltas juntos é o estado normal de uma amizade nova.
 */
export function usePlayingPartners(): UseQueryResult<Map<string, number>> {
  return useQuery({
    queryKey: qk.playingPartners,
    queryFn: async () => {
      const rows = await groupRoundsApi.partners();
      return new Map(rows.map((r) => [r.userId, r.rounds]));
    },
    staleTime: 5 * 60_000,
  });
}

/** Busca de jogadores. `enabled` espelha a regra do servidor: sem termo, não há requisição. */
export function useUserSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: qk.userSearch(q),
    queryFn: () => socialApi.search(q),
    enabled: q.length > 0,
    staleTime: 15_000,
  });
}

export function useRoundComments(roundId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.comments(roundId ?? ''),
    queryFn: () => socialApi.comments(roundId as string),
    enabled: Boolean(roundId) && enabled,
  });
}

// ----------------------------------------------------------- volta em grupo

/** De quanto em quanto tempo o cartão do flight se atualiza sozinho. */
const GROUP_ROUND_POLL_MS = 20_000;

/**
 * O flight, recarregado sozinho enquanto a volta está aberta.
 *
 * É o "tempo real" desta fase, e é honesto quanto ao que é: uma recarga a cada 20 segundos, não
 * um canal. Basta para o que a tela promete — ver o número que o outro jogador acabou de marcar —
 * e não pede WebSocket nem presença. `refetchIntervalInBackground` fica no padrão (falso), então
 * uma aba esquecida aberta não fica batendo na API a volta inteira.
 *
 * O intervalo morre quando a volta fecha: `false` desliga o timer, e uma volta concluída não muda
 * mais.
 */
export function useGroupRound(id: string | undefined) {
  return useQuery({
    queryKey: qk.groupRound(id ?? ''),
    queryFn: () => groupRoundsApi.get(id as string),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data?.status === 'in_progress' ? GROUP_ROUND_POLL_MS : false,
  });
}

/**
 * Descobre se uma volta faz parte de um flight. `null` é resposta válida — a maioria das voltas
 * é individual —, e é por isso que a query fica em `data === null` em vez de em erro.
 */
export function useGroupRoundByRound(roundId: string | undefined) {
  return useQuery({
    queryKey: qk.groupRoundByRound(roundId ?? ''),
    queryFn: () => groupRoundsApi.byRound(roundId as string),
    enabled: Boolean(roundId),
  });
}

// ------------------------------------------------- o jogo de outra pessoa

export function usePlayerProfile(userId: string | undefined) {
  return useQuery({
    queryKey: qk.playerProfile(userId ?? ''),
    queryFn: () => playersApi.profile(userId as string),
    enabled: Boolean(userId),
  });
}

/**
 * Todas as voltas concluídas de outro jogador.
 *
 * Mesma forma do `useAllRounds`: pagina até o fim porque os filtros do canvas (Todas / Com você /
 * campo) e o quadro de estatísticas trabalham sobre o conjunto inteiro, no cliente. Uma cópia em
 * cache serve ao perfil e à lista completa.
 */
export function usePlayerRounds(userId: string | undefined) {
  return useQuery({
    queryKey: qk.playerRounds(userId ?? ''),
    queryFn: async () => {
      const { items, truncated } = await fetchAllPages((page, limit) =>
        playersApi.rounds(userId as string, { page, limit }),
      );
      return { rounds: items as RoundSummary[], truncated };
    },
    enabled: Boolean(userId),
  });
}

export function useRoundsTogether(userId: string | undefined) {
  return useQuery({
    queryKey: qk.roundsTogether(userId ?? ''),
    queryFn: () => playersApi.roundsTogether(userId as string),
    enabled: Boolean(userId),
  });
}

export function useGuestPlayers() {
  return useQuery({ queryKey: qk.guestPlayers, queryFn: guestPlayersApi.list });
}

/** Completed rounds, newest first — the ordering every screen wants. */
export function completedRounds(rounds: RoundSummary[]): RoundSummary[] {
  return rounds
    .filter((r) => r.status === 'completed')
    .sort((a, b) => byDateDesc(a.completedAt ?? a.startedAt, b.completedAt ?? b.startedAt));
}

/**
 * As voltas que podem virar estatística pessoal.
 *
 * O scramble fica de fora: a dupla joga **uma bola só**, então aquele 45 não é o seu jogo — é o
 * do time. O servidor já o exclui do handicap; deixá-lo entrar no score médio ou no GIR seria
 * excluir da conta que importa e incluir na que aparece. A volta continua na lista de voltas,
 * com a etiqueta de grupo: ela aconteceu, só não mede você.
 */
export function statsEligibleRounds(rounds: RoundSummary[]): RoundSummary[] {
  return completedRounds(rounds).filter((r) => r.countsForHandicap);
}

export function inProgressRounds(rounds: RoundSummary[]): RoundSummary[] {
  return rounds
    .filter((r) => r.status === 'in_progress')
    .sort((a, b) => byDateDesc(a.startedAt, b.startedAt));
}

function byDateDesc(a: string, b: string): number {
  return new Date(b).getTime() - new Date(a).getTime();
}
